import { BackArrowIcon } from "@/components/atoms/BackArrowIcon";
import { Logo } from "@/components/atoms/Logo";
import { Typography } from "@/components/atoms/Typography";
import { UIColorTokens } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { router, useNavigation, usePathname } from "expo-router";
import React from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { BaseHeader } from "./BaseHeader";

export interface LogoHeaderProps {
  showBackButton?: boolean;
  onBackPress?: (() => void) | undefined;
  showLanguageSwitch?: boolean;
  onLanguagePress?: ((language: "en" | "ar") => void) | undefined;
  disableBack?: boolean; // externally disable
  hideBackIfRoot?: boolean; // auto-hide when cannot go back
  hideLogo?: boolean; // auto-hide when cannot go back
  logoColor?: keyof UIColorTokens;
  backButtonColor?: keyof UIColorTokens;
  languageTextColor?: keyof UIColorTokens;
}

const AUTHENTICATED_HOME = "/(auth)/(home)";
const IS_WEB = Platform.OS === "web";

export const LogoHeader = React.memo<LogoHeaderProps>(function LogoHeader({
  showBackButton = true,
  onBackPress,
  showLanguageSwitch = true,
  onLanguagePress,
  disableBack = false,
  hideBackIfRoot = true,
  hideLogo = false,
  logoColor = "bloomRed",
  backButtonColor = "fontPrimary",
  languageTextColor = "fontPrimary",
}) {
  const colors = useUIColors();
  const navigation = useNavigation();
  const pathname = usePathname();
  const { currentLanguage, toggleLanguage } = useTranslation();
  const { isRTL } = useI18nContext();
  const isAuthenticated = useAppStore((state) => state.auth.isAuthenticated);
  // Web-only: browser refresh/deep-link empties history; native stack is unchanged.
  const webAuthBackFallback = IS_WEB && isAuthenticated;

  // Check if we can go back
  const isRoot = !pathname || pathname === "/";
  const navCanGoBack =
    typeof (navigation as any)?.canGoBack === "function"
      ? (navigation as any).canGoBack()
      : false;

  // Default back handler using navigation (web refresh / deep-link: no stack → home)
  const handleDefaultBack = () => {
    if (navCanGoBack && (navigation as any)?.goBack) {
      (navigation as any).goBack();
      return;
    }
    if (webAuthBackFallback) {
      try {
        router.replace(AUTHENTICATED_HOME as never);
      } catch (error) {
        logger.warn("LogoHeader", "Fallback navigate to home failed", error);
      }
    }
  };

  // Web logged-in: always show back (history empty after refresh / direct URL).
  // Native + guests: keep hideBackIfRoot / canGoBack behavior.
  const shouldShowBack =
    showBackButton &&
    (Boolean(onBackPress) ||
      webAuthBackFallback ||
      !hideBackIfRoot ||
      (!isRoot && navCanGoBack));

  // Use custom handler if provided, otherwise use default navigation
  const backHandler = onBackPress || handleDefaultBack;

  // Handler that respects disableBack
  const handleBackPress = () => {
    if (!disableBack) {
      backHandler();
    }
  };

  // Language toggle handler
  const handleLanguageToggle = async () => {
    try {
      logger.info("LogoHeader", "Toggling language", { currentLanguage });
      const newLanguage = await toggleLanguage();
      logger.info("LogoHeader", "Language toggled successfully", {
        newLanguage,
      });
      onLanguagePress?.(newLanguage);
    } catch (error) {
      logger.error("LogoHeader", "Failed to toggle language:", error);
    }
  };

  // Render language button based on RTL state
  const renderLanguageButton = () => {
    if (!showLanguageSwitch) return null;

    // Show opposite language text: when English is selected, show Arabic; when Arabic is selected, show English
    // Position: English button on right when isRTL is false, Arabic button on left when isRTL is true
    const displayText = currentLanguage === "en" ? "العربية" : "English";

    return (
      <TouchableOpacity
        onPress={handleLanguageToggle}
        style={styles.languageButton}
        activeOpacity={0.7}
      >
        <Typography
          variant="body"
          numberOfLines={1}
          style={[
            styles.languageText,
            {
              color: colors[languageTextColor],
            },
          ]}
        >
          {displayText}
        </Typography>
      </TouchableOpacity>
    );
  };

  return (
    <BaseHeader
      leading={
        <View style={styles.leadingContainer}>
          {shouldShowBack && (
            <TouchableOpacity
              onPress={handleBackPress}
              style={[
                styles.buttonContainer,
                { opacity: disableBack ? 0.5 : 1 },
              ]}
              disabled={disableBack}
            >
              <BackArrowIcon size={24} color={colors[backButtonColor]} />
            </TouchableOpacity>
          )}
        </View>
      }
      middle={
        hideLogo ? (
          <View style={styles.placeholder} />
        ) : (
          <View style={styles.logoContainer}>
            <Logo color={logoColor} size="medium" />
          </View>
        )
      }
      trailing={
        renderLanguageButton() || <View style={styles.placeholder} />
      }
    />
  );
});

const styles = StyleSheet.create({
  leadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  buttonContainer: {
    height: 40,
    width: 40,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  languageButton: {
    minHeight: 40,
    justifyContent: "center",
    paddingVertical: 4,
  },
  languageText: {
    fontWeight: "500",
    lineHeight: 20,
    includeFontPadding: false,
    textAlignVertical: "center",
  },
  placeholder: {
    height: 40,
    width: 60,
  },
  logoContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default LogoHeader;
